import DOMPurify from "dompurify";

const ALLOWED_TAGS = [
  "p",
  "br",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "s",
  "ul",
  "ol",
  "li",
  "a",
  "blockquote",
  "code",
  "pre",
  "span",
  "img",
];

/**
 * Pasted images are embedded as base64 rather than uploaded, so the bytes live
 * in the answer JSON and are re-fetched on every load of the form response.
 * Anything past this is refused outright rather than silently bloating the
 * response — see the paste guard in DiscussionThreadEditor.vue.
 */
export const maxPastedImageBytes = 2 * 1024 * 1024;

/**
 * Only raster image data is embeddable. SVG is excluded deliberately: an SVG
 * data URI can carry script, so allowing it here would reopen the XSS hole the
 * tag allowlist closes.
 */
const SAFE_IMAGE_SRC =
  /^data:image\/(?:png|jpeg|jpg|gif|webp);base64,[a-z0-9+/=\s]+$/i;

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  // Links in a comment must not navigate the form away mid-response — a
  // partially answered form would lose the user's place — so every anchor opens
  // in a new tab. rel guards against the new tab reaching back via
  // window.opener.
  if (node.tagName === "A") {
    // Widening ALLOWED_URI_REGEXP to admit data: for images would otherwise let
    // a data:text/html link through here, so href keeps the stricter rule.
    if (/^\s*data:/i.test(node.getAttribute("href") ?? "")) {
      node.removeAttribute("href");
    }
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }

  // ALLOWED_URI_REGEXP applies to every attribute at once, so it can't say
  // "data: is fine for img@src but not for a@href". Enforce that split here
  // instead, and drop any image whose src isn't inline raster data.
  if (node.tagName === "IMG") {
    const src = node.getAttribute("src") ?? "";
    if (!SAFE_IMAGE_SRC.test(src.replace(/\s+/g, ""))) {
      node.remove();
    }
  }
});

/**
 * Comments posted before the rich-text change are plain text, and plain text
 * that happens to contain `<` or `&` is not valid HTML. Rendering it as HTML
 * would silently destroy content — "Use <brackets> here" sanitizes down to
 * "Use  here", because `<brackets>` parses as an unknown tag and is stripped.
 *
 * A legacy comment is one containing none of the tags the editor can produce.
 * Matching *any* `<word>` would be wrong — plain text like "Use <brackets>
 * here" would be misread as markup and the bracketed word silently dropped —
 * so this only counts tags the editor itself emits.
 */
const HTML_TAG_PATTERN = new RegExp(
  `<\\/?(?:${ALLOWED_TAGS.join("|")})(?:\\s[^>]*)?\\/?>`,
  "i",
);

const looksLikeHtml = (text: string): boolean => HTML_TAG_PATTERN.test(text);

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    // Plain-text comments used newlines for line breaks; HTML collapses those
    // to spaces, so preserve them as the <br> the reader originally saw.
    .replace(/\r?\n/g, "<br>");

/**
 * Comment bodies are rich text authored by other users and rendered with
 * `v-html`, so they must be sanitized before display. Legacy plain-text
 * comments are escaped first so their content survives intact.
 */
export const sanitizeCommentHtml = (
  html: string | null | undefined,
): string => {
  const text = html ?? "";

  return DOMPurify.sanitize(looksLikeHtml(text) ? text : escapeHtml(text), {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ["href", "target", "rel", "src", "alt"],
    // data: is admitted here only so pasted images survive the URI gate; the
    // afterSanitizeAttributes hook is what confines it to img@src raster data.
    ALLOWED_URI_REGEXP: /^(?:https?|mailto|data):/i,
    ADD_ATTR: ["target"],
  });
};

/**
 * TinyMCE never yields a truly empty string — an untouched editor still emits
 * markup like `<p><br></p>`, which `String.trim()` reports as non-empty. Strip
 * tags and entities so "is there anything to post?" checks on the raw HTML
 * don't let blank comments through.
 */
export const isCommentEmpty = (html: string | null | undefined): boolean => {
  if (!html) {
    return true;
  }

  // An image on its own is a legitimate comment even though it has no text, so
  // it has to count as non-empty or Post would stay disabled for it. Ask the
  // sanitizer what actually survives rather than matching src with a second
  // regex — anything the sanitizer drops (remote urls, svg, malformed data
  // uris) would otherwise let the user post a comment that renders empty.
  const sanitized = sanitizeCommentHtml(html);
  if (/<img\b/i.test(sanitized)) {
    return false;
  }

  // Decode entities rather than deleting them: stripping `&[a-z]+;` outright
  // treated a comment of "&" (escaped to "&amp;") as empty, since the whole
  // entity vanished instead of becoming the character it represents.
  const decoded = new DOMParser().parseFromString(
    sanitized.replace(/<[^>]*>/g, ""),
    "text/html",
  ).documentElement.textContent;

  // &nbsp; decodes to a non-breaking space, which trim() does not touch.
  return (decoded ?? "").replace(/\u00a0/g, " ").trim() === "";
};
